/**
 * 甘特图主组件
 *
 * 组合所有子组件，实现完整的甘特图功能
 */

import { Loader2 } from 'lucide-react'
import * as React from 'react'

import { useToast } from '@/components/ui/toast'
import { useGanttData , useGanttState , useUpdateIssue } from '@/hooks'
import { cn } from '@/lib/utils'

import { GanttTimeline } from './gantt-timeline'
import { GanttToolbar } from './gantt-toolbar'
import { TaskList } from './task-list'
import { DEFAULT_ROW_HEIGHT } from './types'

import type { TaskDates, DateRangePreset } from './types'

interface GanttChartProps {
  /** 项目 ID */
  projectId: string
  /** 任务点击回调 */
  onTaskClick?: (taskId: string) => void
  /** 里程碑点击回调 */
  onMilestoneClick?: (milestoneId: string) => void
  /** 类名 */
  className?: string
}

export function GanttChart({
  projectId,
  onTaskClick,
  onMilestoneClick,
  className,
}: GanttChartProps) {
  const { success: showSuccess, error: showError } = useToast()
  const timelineRef = React.useRef<HTMLDivElement>(null)

  // 状态管理
  const {
    zoomLevel,
    setZoomLevel,
    dateRange,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    showDependencies,
    toggleDependencies,
    expandedGroups,
    toggleGroup,
    scrollToToday,
    navigate,
    dateRangePreset,
    setDateRangePreset,
    autoFit,
    hasTasksOutsideView,
  } = useGanttState({ projectId })

  // 数据获取
  const {
    tasks,
    milestones,
    groups,
    isLoading,
    error,
    visibleCount,
    totalCount,
  } = useGanttData({
    projectId,
    filters,
    groupBy,
    expandedGroups,
  })

  // 任务更新
  const updateIssue = useUpdateIssue()

  // 滚动同步
  const [scrollTop, setScrollTop] = React.useState(0)

  // 处理滚动
  const handleTimelineScroll = React.useCallback(
    (newScrollTop: number, _scrollLeft: number) => {
      setScrollTop(newScrollTop)
    },
    []
  )

  const handleTaskListScroll = React.useCallback((newScrollTop: number) => {
    setScrollTop(newScrollTop)
  }, [])

  // 处理任务拖拽更新
  const handleTaskDrag = React.useCallback(
    (taskId: string, newDates: TaskDates) => {
      updateIssue.mutate(
        {
          id: taskId,
          data: {
            startDate: newDates.startDate,
            dueDate: newDates.endDate,
          },
        },
        {
          onSuccess: () => {
            showSuccess('任务日期已更新', `${newDates.startDate} ~ ${newDates.endDate}`)
          },
          onError: () => {
            showError('更新失败', '无法更新任务日期，请重试')
          },
        }
      )
    },
    [updateIssue, showSuccess, showError]
  )

  // 处理任务点击
  const handleTaskClick = React.useCallback(
    (taskId: string) => {
      onTaskClick?.(taskId)
    },
    [onTaskClick]
  )

  // 处理里程碑点击
  const handleMilestoneClick = React.useCallback(
    (milestoneId: string) => {
      onMilestoneClick?.(milestoneId)
    },
    [onMilestoneClick]
  )

  // 键盘导航 (已移除 useGanttKeyboard)

  // 首次加载时自动适配到任务日期范围
  const hasAutoFitted = React.useRef(false)
  React.useEffect(() => {
    if (!hasAutoFitted.current && tasks.length > 0 && !isLoading) {
      // 检查是否有任务在当前视图外，如果有则自动适配
      if (hasTasksOutsideView(tasks)) {
        autoFit(tasks)
      }
      hasAutoFitted.current = true
    }
  }, [tasks, isLoading, hasTasksOutsideView, autoFit])

  // 处理自动适配
  const handleAutoFit = React.useCallback(() => {
    autoFit(tasks)
  }, [autoFit, tasks])

  // 检查是否有任务在视图外
  const tasksOutsideView = React.useMemo(() => {
    return hasTasksOutsideView(tasks)
  }, [hasTasksOutsideView, tasks])

  // 处理日期范围预设变更
  const handleDateRangePresetChange = React.useCallback(
    (preset: DateRangePreset) => {
      if (preset === 'auto') {
        autoFit(tasks)
      } else {
        setDateRangePreset(preset)
      }
    },
    [setDateRangePreset, autoFit, tasks]
  )

  // 加载状态
  if (isLoading) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <div className="text-center">
          <p className="text-destructive">加载失败</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {error.message || '无法加载甘特图数据'}
          </p>
        </div>
      </div>
    )
  }

  // 空状态
  if (tasks.length === 0 && totalCount === 0) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <div className="text-center">
          <p className="text-muted-foreground">暂无任务</p>
          <p className="mt-1 text-sm text-muted-foreground">
            创建任务后将在此显示甘特图
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* 工具栏 */}
      <GanttToolbar
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        filters={filters}
        onFiltersChange={setFilters}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        onTodayClick={scrollToToday}
        onNavigate={navigate}
        showDependencies={showDependencies}
        onToggleDependencies={toggleDependencies}
        visibleCount={visibleCount}
        totalCount={totalCount}
        dateRangePreset={dateRangePreset}
        onDateRangePresetChange={handleDateRangePresetChange}
        onAutoFitClick={handleAutoFit}
        hasTasksOutsideView={tasksOutsideView}
      />

      {/* 主体区域 */}
      <div className="flex flex-1 overflow-hidden">
        {/* 左侧任务列表 */}
        <TaskList
          tasks={tasks}
          groups={groups}
          groupBy={groupBy}
          onTaskClick={handleTaskClick}
          onToggleGroup={toggleGroup}
          rowHeight={DEFAULT_ROW_HEIGHT}
          scrollTop={scrollTop}
          onScroll={handleTaskListScroll}
        />

        {/* 右侧时间线 */}
        <div ref={timelineRef} className="flex-1 overflow-hidden">
          <GanttTimeline
            tasks={tasks}
            groups={groups}
            groupBy={groupBy}
            milestones={milestones}
            zoomLevel={zoomLevel}
            dateRange={dateRange}
            onTaskDrag={handleTaskDrag}
            onTaskClick={handleTaskClick}
            onMilestoneClick={handleMilestoneClick}
            showDependencies={showDependencies}
            rowHeight={DEFAULT_ROW_HEIGHT}
            scrollTop={scrollTop}
            onScroll={handleTimelineScroll}
          />
        </div>
      </div>
    </div>
  )
}
